/**
 * Phase 1.5.4 — 営業日設定画面（admin SPA）。
 *
 * 機能：
 * - 平日・土日の営業時間編集（HH:MM 入力 + 30/60 分枠選択）
 * - 休業日リスト表示（kind 別バッジ）
 * - 休業日追加（モーダル）
 * - 休業日削除（確認モーダル）
 * - 「2026 年祝日テンプレ一括登録」ボタン
 */

import {
  getBusinessSettings,
  updateBusinessHours,
  upsertHolidays,
  deleteHoliday,
  seedNationalHolidays,
  type BusinessSettingsDto,
  type HolidayDto,
} from "../api";
import { setRoot, escapeHtml, showToast, showError } from "../render";
import { state } from "../store";

const KIND_LABELS: Record<HolidayDto["kind"], string> = {
  national: "国民の祝日",
  custom: "定例休",
  temporary: "臨時休業",
};

const KIND_BADGE_CLASS: Record<HolidayDto["kind"], string> = {
  national: "bg-purple-100 text-purple-800",
  custom: "bg-gray-200 text-gray-800",
  temporary: "bg-amber-100 text-amber-800",
};

let cachedSettings: BusinessSettingsDto | null = null;

export async function renderBusinessSettings(): Promise<void> {
  state.view = "settings";
  setRoot(`
    ${headerHtml()}
    <div class="text-center text-gray-500 mt-8">読み込み中...</div>
  `);
  bindBackButton();

  try {
    cachedSettings = await getBusinessSettings();
  } catch (e: any) {
    showError(`営業日設定の取得に失敗しました：${e?.message ?? e}`);
    return;
  }

  paint();
}

function paint(): void {
  if (!cachedSettings) return;
  setRoot(`
    ${headerHtml()}
    ${defaultBannerHtml(cachedSettings)}
    ${hoursSectionHtml("weekday", "平日", cachedSettings.weekday_hours)}
    ${hoursSectionHtml("weekend", "土日", cachedSettings.weekend_hours)}
    ${holidaysSectionHtml(cachedSettings.holidays)}
  `);
  bindBackButton();
  bindHoursForm("weekday");
  bindHoursForm("weekend");
  bindHolidayActions();
}

function headerHtml(): string {
  return `
    <header class="mb-3 pb-2 border-b flex items-center justify-between">
      <button id="btn-back-list"
        class="text-sm text-gray-500 hover:text-gray-700 px-2 py-1">
        ‹ 一覧へ
      </button>
      <h1 class="text-lg font-bold text-gray-900">⚙ 営業日設定</h1>
      <span class="w-16"></span>
    </header>
  `;
}

function defaultBannerHtml(s: BusinessSettingsDto): string {
  if (!s.is_default) return "";
  return `
    <div class="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
      ⚠️ 現在は既定値で運用中です。<br>
      営業時間や休業日を変更したら下のボタンで保存してください。
    </div>
  `;
}

// ===== 営業時間セクション =====

// 時（00-23）と分（00/15/30/45）の選択肢を生成。
// Chrome の <input type="time"> は step="900" を尊重せず 1 分単位の picker を出すため、
// select 要素で完全自前実装する（Phase 1.5.4 祐輔さん UX フィードバック反映）。
const HOUR_VALUES = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, "0")
);
const MINUTE_VALUES = ["00", "15", "30", "45"] as const;

function buildSelectOptions(values: readonly string[], selected: string): string {
  return values
    .map(
      (v) =>
        `<option value="${v}"${v === selected ? " selected" : ""}>${v}</option>`
    )
    .join("");
}

function hoursSectionHtml(
  dayType: "weekday" | "weekend",
  label: string,
  hours: BusinessSettingsDto["weekday_hours"]
): string {
  const idPrefix = `hours-${dayType}`;
  const [startH, startM] = hours.start.split(":");
  const [endH, endM] = hours.end.split(":");
  const startMSafe = MINUTE_VALUES.includes(startM as (typeof MINUTE_VALUES)[number])
    ? startM
    : "00";
  const endMSafe = MINUTE_VALUES.includes(endM as (typeof MINUTE_VALUES)[number])
    ? endM
    : "00";

  return `
    <div class="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div class="font-semibold text-gray-900 mb-3">📅 ${escapeHtml(label)}の営業時間</div>

      <div class="mb-3">
        <span class="text-xs text-gray-500 block mb-1">開始</span>
        <div class="flex gap-2 items-center">
          <select id="${idPrefix}-start-hour"
            class="flex-1 border border-gray-300 rounded px-2 py-2 text-base">
            ${buildSelectOptions(HOUR_VALUES, startH)}
          </select>
          <span class="text-gray-500 font-bold">:</span>
          <select id="${idPrefix}-start-minute"
            class="flex-1 border border-gray-300 rounded px-2 py-2 text-base">
            ${buildSelectOptions(MINUTE_VALUES, startMSafe)}
          </select>
        </div>
      </div>

      <div class="mb-3">
        <span class="text-xs text-gray-500 block mb-1">終了</span>
        <div class="flex gap-2 items-center">
          <select id="${idPrefix}-end-hour"
            class="flex-1 border border-gray-300 rounded px-2 py-2 text-base">
            ${buildSelectOptions(HOUR_VALUES, endH)}
          </select>
          <span class="text-gray-500 font-bold">:</span>
          <select id="${idPrefix}-end-minute"
            class="flex-1 border border-gray-300 rounded px-2 py-2 text-base">
            ${buildSelectOptions(MINUTE_VALUES, endMSafe)}
          </select>
        </div>
      </div>

      <p class="text-xs text-gray-500 mb-2">
        ※ 時刻は 15 分単位で設定できます（00 / 15 / 30 / 45）
      </p>

      <label class="block mb-3">
        <span class="text-xs text-gray-500">1 枠の時間</span>
        <select id="${idPrefix}-slot"
          class="w-full border border-gray-300 rounded px-2 py-2 text-base">
          <option value="15" ${hours.slot_minutes === 15 ? "selected" : ""}>15 分</option>
          <option value="30" ${hours.slot_minutes === 30 ? "selected" : ""}>30 分</option>
          <option value="60" ${hours.slot_minutes === 60 ? "selected" : ""}>60 分</option>
        </select>
      </label>
      <button id="${idPrefix}-save"
        class="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold">
        保存
      </button>
    </div>
  `;
}

function bindHoursForm(dayType: "weekday" | "weekend"): void {
  const btn = document.getElementById(`hours-${dayType}-save`);
  if (!btn) return;
  btn.addEventListener("click", async () => {
    // 時 / 分 を別 select から取得して "HH:MM" を組み立てる（Phase 1.5.4 UX 改善）
    const startHourEl = document.getElementById(`hours-${dayType}-start-hour`) as HTMLSelectElement | null;
    const startMinEl = document.getElementById(`hours-${dayType}-start-minute`) as HTMLSelectElement | null;
    const endHourEl = document.getElementById(`hours-${dayType}-end-hour`) as HTMLSelectElement | null;
    const endMinEl = document.getElementById(`hours-${dayType}-end-minute`) as HTMLSelectElement | null;
    const slotEl = document.getElementById(`hours-${dayType}-slot`) as HTMLSelectElement | null;
    if (!startHourEl || !startMinEl || !endHourEl || !endMinEl || !slotEl) return;

    const start_time = `${startHourEl.value}:${startMinEl.value}`;
    const end_time = `${endHourEl.value}:${endMinEl.value}`;
    const slot_minutes = Number(slotEl.value);

    (btn as HTMLButtonElement).disabled = true;
    btn.textContent = "保存中...";
    try {
      await updateBusinessHours(dayType, { start_time, end_time, slot_minutes });
      showToast(`${dayType === "weekday" ? "平日" : "土日"}の営業時間を保存しました`, "success");
      // 再取得
      cachedSettings = await getBusinessSettings();
      paint();
    } catch (e: any) {
      showError(`保存に失敗しました：${e?.message ?? e}`);
      (btn as HTMLButtonElement).disabled = false;
      btn.textContent = "保存";
    }
  });
}

// ===== 休業日セクション =====

function holidaysSectionHtml(holidays: HolidayDto[]): string {
  const itemsHtml =
    holidays.length === 0
      ? `<div class="text-sm text-gray-500 py-3 text-center">登録された休業日はありません</div>`
      : holidays.map(holidayItemHtml).join("");

  return `
    <div class="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div class="flex items-center justify-between mb-3">
        <div class="font-semibold text-gray-900">🚫 休業日（${holidays.length} 件）</div>
        <button id="btn-add-holiday"
          class="text-sm bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded">
          ＋ 追加
        </button>
      </div>
      <div id="holiday-list" class="divide-y divide-gray-200">
        ${itemsHtml}
      </div>
      <div class="mt-4 pt-3 border-t border-gray-200">
        <button id="btn-seed-national"
          class="w-full text-sm text-emerald-700 hover:bg-emerald-50 px-3 py-2 rounded border border-emerald-300">
          ⟳ 2026・2027 年の国民の祝日をまとめて登録
        </button>
      </div>
    </div>
  `;
}

function holidayItemHtml(h: HolidayDto): string {
  const labelDisplay = h.label ? `<span class="text-xs text-gray-600 ml-2">${escapeHtml(h.label)}</span>` : "";
  return `
    <div class="flex items-center justify-between py-2" data-date="${escapeHtml(h.date)}">
      <div class="min-w-0 flex-1">
        <div class="text-sm font-medium text-gray-900">${escapeHtml(formatDateWithWeekday(h.date))}</div>
        <div class="text-xs">
          <span class="px-2 py-0.5 rounded ${KIND_BADGE_CLASS[h.kind]}">
            ${escapeHtml(KIND_LABELS[h.kind])}
          </span>
          ${labelDisplay}
        </div>
      </div>
      <button data-delete-date="${escapeHtml(h.date)}"
        class="text-red-600 hover:bg-red-50 px-2 py-1 rounded text-sm"
        aria-label="削除">
        ✕
      </button>
    </div>
  `;
}

function bindHolidayActions(): void {
  document.getElementById("btn-add-holiday")?.addEventListener("click", async () => {
    const { openHolidayAddModal } = await import("./holidayAddModal");
    openHolidayAddModal(async () => {
      cachedSettings = await getBusinessSettings();
      paint();
    });
  });

  document.getElementById("btn-seed-national")?.addEventListener("click", async () => {
    if (!confirm("2026・2027 年の国民の祝日をまとめて登録します。すでに登録されている日付は変更しません。よろしいですか？")) return;
    try {
      const r = await seedNationalHolidays();
      showToast(`国民の祝日 ${r.seeded} 件分の登録を試みました（重複は自動でスキップ）`, "success");
      cachedSettings = await getBusinessSettings();
      paint();
    } catch (e: any) {
      showError(`登録に失敗しました：${e?.message ?? e}`);
    }
  });

  // 削除ボタン（イベント委譲）
  document.getElementById("holiday-list")?.addEventListener("click", async (ev) => {
    const target = (ev.target as HTMLElement).closest("[data-delete-date]") as HTMLElement | null;
    if (!target) return;
    const date = target.getAttribute("data-delete-date");
    if (!date) return;
    if (!confirm(`${date} の休業日を削除します。よろしいですか？`)) return;
    try {
      await deleteHoliday(date);
      showToast("休業日を削除しました", "success");
      cachedSettings = await getBusinessSettings();
      paint();
    } catch (e: any) {
      showError(`削除に失敗しました：${e?.message ?? e}`);
    }
  });
}

// ===== 共通 =====

function bindBackButton(): void {
  document.getElementById("btn-back-list")?.addEventListener("click", async () => {
    const { renderList } = await import("./list");
    await renderList();
  });
}

function formatDateWithWeekday(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${Number(mo)}月${Number(d)}日(${w})`;
}
